import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { 
  GraduationCap,
  Play,
  CheckCircle,
  Circle,
  ArrowRight,
  ArrowLeft,
  Trophy,
  Clock,
  BookOpen,
  CaretDown,
  CaretUp,
  Sparkle,
  Users,
  Target,
  Rocket,
  X
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function TrainingPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [trainingContent, setTrainingContent] = useState(null);
  const [progress, setProgress] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [expandedModules, setExpandedModules] = useState({});
  
  // For admins viewing other roles
  const [selectedRole, setSelectedRole] = useState(null);
  const [allContent, setAllContent] = useState(null);
  
  // Team progress (admin)
  const [teamProgress, setTeamProgress] = useState([]);
  const [showTeamProgress, setShowTeamProgress] = useState(false);

  const loadTrainingData = useCallback(async () => {
    const userRole = user?.role;
    setLoading(true);
    try {
      const [contentRes, progressRes] = await Promise.all([
        userRole === 'admin' 
          ? api.get('/training/all-content')
          : api.get(`/training/content/${userRole}`),
        api.get('/training/progress')
      ]);
      
      if (userRole === 'admin') {
        setAllContent(contentRes.data);
        setSelectedRole('admin');
        setTrainingContent(contentRes.data.admin);
      } else {
        setTrainingContent(contentRes.data);
      }
      setProgress(progressRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        process.env.NODE_ENV === "development" && console.error('Error loading training:', error);
      }
      toast.error('Failed to load training content');
    } finally {
      setLoading(false);
    }
  }, [user?.role]); // api is module-level, toast is external

  const loadTeamProgress = async () => {
    try {
      const response = await api.get('/training/team-progress');
      setTeamProgress(response.data.team_progress || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading team progress:', error);
    }
  };

  useEffect(() => {
    loadTrainingData();
  }, [loadTrainingData]);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadTeamProgress();
    }
  }, [user?.role]);

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    setTrainingContent(allContent[role]);
    setSelectedModule(null);
    setCurrentStepIndex(0);
  };

  const startModule = (module) => {
    setSelectedModule(module);
    // Find first uncompleted step
    const completedSteps = progress?.completed_steps?.[module.id] || [];
    const firstIncomplete = module.steps.findIndex((_, idx) => !completedSteps.includes(idx));
    setCurrentStepIndex(firstIncomplete >= 0 ? firstIncomplete : 0);
  };

  const completeStep = async () => {
    try {
      const response = await api.post('/training/complete-step', {
        module_id: selectedModule.id,
        step_index: currentStepIndex
      });
      
      setProgress(prev => ({
        ...prev,
        completed_steps: response.data.completed_steps,
        completion_percentage: response.data.completion_percentage
      }));
      
      // Move to next step or close module
      if (currentStepIndex < selectedModule.steps.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      } else {
        toast.success('Module completed! 🎉');
        setSelectedModule(null);
        loadTrainingData();
      }
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const resetProgress = async () => {
    if (!window.confirm('Are you sure you want to reset your training progress?')) return;
    
    try {
      await api.post('/training/reset');
      toast.success('Progress reset');
      loadTrainingData();
    } catch (error) {
      toast.error('Failed to reset progress');
    }
  };

  const toggleModuleExpand = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const isStepCompleted = (moduleId, stepIndex) => {
    return progress?.completed_steps?.[moduleId]?.includes(stepIndex);
  };

  const isModuleCompleted = (moduleId) => {
    return progress?.completed_modules?.includes(moduleId);
  };

  const getModuleProgress = (module) => {
    const completed = progress?.completed_steps?.[module.id]?.length || 0;
    const total = module.steps.length;
    return { completed, total, percentage: Math.round((completed / total) * 100) };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="training-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <GraduationCap className="w-8 h-8 text-teal-600" />
              Staff Training Portal
            </h1>
            <p className="text-gray-600 mt-1">
              Step-by-step training for {trainingContent?.role_name || 'your role'}
            </p>
          </div>
          
          {user?.role === 'admin' && (
            <button
              onClick={() => setShowTeamProgress(!showTeamProgress)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Users className="w-5 h-5" />
              Team Progress
            </button>
          )}
        </div>

        {/* Role Selector (Admin Only) */}
        {user?.role === 'admin' && allContent && (
          <div className="mt-4 flex gap-2">
            {Object.keys(allContent).map(role => (
              <button
                key={role}
                onClick={() => handleRoleChange(role)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedRole === role
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {role.toUpperCase()} Training
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Progress Overview */}
      <div className="bg-gradient-to-r from-teal-500 to-blue-600 rounded-xl p-6 mb-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold mb-1">Your Training Progress</h2>
            <p className="text-teal-100">
              {progress?.completed_modules?.length || 0} of {trainingContent?.modules?.length || 0} modules completed
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{progress?.completion_percentage || 0}%</div>
            <p className="text-teal-100 text-sm">Complete</p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4 h-3 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white rounded-full transition-all duration-500"
            style={{ width: `${progress?.completion_percentage || 0}%` }}
          />
        </div>
        
        {progress?.completion_percentage === 100 && (
          <div className="mt-4 flex items-center gap-2 text-yellow-300">
            <Trophy className="w-6 h-6" />
            <span className="font-semibold">Congratulations! You&apos;ve completed all training modules!</span>
          </div>
        )}
      </div>

      {/* Team Progress Modal (Admin) */}
      {showTeamProgress && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Users className="w-5 h-5 text-teal-600" />
                Team Training Progress
              </h3>
              <button onClick={() => setShowTeamProgress(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[60vh]">
              {teamProgress.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Staff Member</th>
                      <th className="text-left py-2 px-3">Role</th>
                      <th className="text-left py-2 px-3">Progress</th>
                      <th className="text-left py-2 px-3">Last Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamProgress.map((member, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-3">
                          <div className="font-medium">{member.user_name}</div>
                          <div className="text-sm text-gray-500">{member.user_email}</div>
                        </td>
                        <td className="py-3 px-3">
                          <span className="px-2 py-1 bg-gray-100 rounded-full text-sm capitalize">
                            {member.user_role}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden w-24">
                              <div 
                                className="h-full bg-teal-500 rounded-full"
                                style={{ width: `${member.completion_percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">{member.completion_percentage}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-sm text-gray-500">
                          {new Date(member.last_activity).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No team members have started training yet.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Module List */}
      {!selectedModule && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-teal-600" />
            Training Modules
          </h2>
          
          {trainingContent?.modules?.map((module, idx) => {
            const moduleProgress = getModuleProgress(module);
            const isComplete = isModuleCompleted(module.id);
            const isExpanded = expandedModules[module.id];
            
            return (
              <div key={module.id} className="bg-white rounded-xl border shadow-sm overflow-hidden">
                {/* Module Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleModuleExpand(module.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isComplete ? 'bg-green-100 text-green-600' : 'bg-teal-100 text-teal-600'
                      }`}>
                        {isComplete ? (
                          <CheckCircle className="w-6 h-6" weight="fill" />
                        ) : (
                          <span className="font-bold">{idx + 1}</span>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{module.title}</h3>
                        <p className="text-sm text-gray-500 mt-1">{module.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {module.duration}
                          </span>
                          <span className="flex items-center gap-1">
                            <Target className="w-4 h-4" />
                            {module.steps.length} steps
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {!isComplete && moduleProgress.completed > 0 && (
                        <div className="text-right">
                          <div className="text-sm font-medium text-teal-600">
                            {moduleProgress.completed}/{moduleProgress.total}
                          </div>
                          <div className="w-20 h-1.5 bg-gray-200 rounded-full mt-1">
                            <div 
                              className="h-full bg-teal-500 rounded-full"
                              style={{ width: `${moduleProgress.percentage}%` }}
                            />
                          </div>
                        </div>
                      )}
                      {isExpanded ? (
                        <CaretUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <CaretDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expanded Steps */}
                {isExpanded && (
                  <div className="border-t bg-gray-50 p-4">
                    <div className="space-y-2 mb-4">
                      {module.steps.map((step, stepIdx) => {
                        const stepComplete = isStepCompleted(module.id, stepIdx);
                        return (
                          <div 
                            key={stepIdx}
                            className={`flex items-center gap-3 p-2 rounded-lg ${
                              stepComplete ? 'bg-green-50' : 'bg-white'
                            }`}
                          >
                            {stepComplete ? (
                              <CheckCircle className="w-5 h-5 text-green-500" weight="fill" />
                            ) : (
                              <Circle className="w-5 h-5 text-gray-300" />
                            )}
                            <span className={stepComplete ? 'text-green-700' : 'text-gray-700'}>
                              {step.title}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => startModule(module)}
                      className={`w-full py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                        isComplete
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          : 'bg-teal-600 text-white hover:bg-teal-700'
                      }`}
                    >
                      {isComplete ? (
                        <>Review Module</>
                      ) : moduleProgress.completed > 0 ? (
                        <>Continue Training <ArrowRight className="w-5 h-5" /></>
                      ) : (
                        <>Start Module <Play className="w-5 h-5" /></>
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Reset Progress */}
          <div className="text-center pt-4">
            <button
              onClick={resetProgress}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Reset training progress
            </button>
          </div>
        </div>
      )}

      {/* Active Module View */}
      {selectedModule && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          {/* Module Header */}
          <div className="p-4 border-b bg-gradient-to-r from-teal-50 to-blue-50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{selectedModule.title}</h2>
                <p className="text-gray-600 text-sm mt-1">
                  Step {currentStepIndex + 1} of {selectedModule.steps.length}
                </p>
              </div>
              <button
                onClick={() => setSelectedModule(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Step Progress */}
            <div className="flex gap-1 mt-4">
              {selectedModule.steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-2 flex-1 rounded-full ${
                    isStepCompleted(selectedModule.id, idx)
                      ? 'bg-green-500'
                      : idx === currentStepIndex
                      ? 'bg-teal-500'
                      : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="p-6">
            <div className="mb-6">
              <div className="flex items-center gap-2 text-teal-600 mb-2">
                <Sparkle className="w-5 h-5" />
                <span className="font-medium">Step {currentStepIndex + 1}</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {selectedModule.steps[currentStepIndex].title}
              </h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                {selectedModule.steps[currentStepIndex].content}
              </p>
            </div>

            {/* Action Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Rocket className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-blue-900">Try it yourself:</h4>
                  <p className="text-blue-800 mt-1">
                    {selectedModule.steps[currentStepIndex].action}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4 border-t">
              <button
                onClick={() => setCurrentStepIndex(Math.max(0, currentStepIndex - 1))}
                disabled={currentStepIndex === 0}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="w-5 h-5" />
                Previous
              </button>
              
              <button
                onClick={completeStep}
                className="flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                {currentStepIndex < selectedModule.steps.length - 1 ? (
                  <>
                    {isStepCompleted(selectedModule.id, currentStepIndex) ? 'Next Step' : 'Mark Complete & Continue'}
                    <ArrowRight className="w-5 h-5" />
                  </>
                ) : (
                  <>
                    Finish Module
                    <CheckCircle className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
